import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Users, UserX, TrendingUp, Target, AlertTriangle, Lightbulb,
  ChevronRight, BarChart3, PieChart as PieChartIcon, Building2,
  FileSpreadsheet, FileText,
} from 'lucide-react';
import { useExportMetrics } from '@/hooks/useExportMetrics';
import { useDiagnosticMetrics } from '@/hooks/useDiagnosticMetrics';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';
import { useRole } from '@/contexts/RoleContext';
import { useOrgFilterStore } from '@/hooks/useOrgFilterStore';

const COLORS = ['hsl(var(--destructive))', 'hsl(var(--warning))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--success))'];

export function DiagnosticMetricsPage() {
  const { currentRole } = useRole();
  const isSuperAdmin = currentRole === 'super_admin';
  const { exportToExcel, exportToPDF } = useExportMetrics();
  const { users, organizations, isLoading, MATURITY_LABELS } = useDiagnosticMetrics();

  const [organizationFilter, setOrganizationFilter] = useOrgFilterStore();
  const [areaFilter, setAreaFilter] = useState<string>('all');

  const orgFiltered = useMemo(() =>
    organizationFilter === 'all' ? users : users.filter(u => u.organization_id === organizationFilter),
    [users, organizationFilter]
  );

  const areaFiltered = useMemo(() =>
    areaFilter === 'all' ? orgFiltered : orgFiltered.filter(u => u.area === areaFilter),
    [orgFiltered, areaFilter]
  );

  const areas = useMemo(() => [...new Set(orgFiltered.map(u => u.area).filter(Boolean) as string[])], [orgFiltered]);

  const withDiagnostic = useMemo(() => areaFiltered.filter(u => u.has_diagnostic), [areaFiltered]);
  const withoutDiagnostic = useMemo(() => areaFiltered.filter(u => !u.has_diagnostic), [areaFiltered]);

  const stats = useMemo(() => {
    const levels = withDiagnostic.map(u => u.maturity_level || 0);
    const avg = levels.length > 0 ? levels.reduce((a, b) => a + b, 0) / levels.length : 0;
    const levelCounts: Record<number, number> = {};
    levels.forEach(l => { levelCounts[l] = (levelCounts[l] || 0) + 1; });
    const predominant = Object.entries(levelCounts).sort((a, b) => b[1] - a[1])[0];
    return {
      totalDiagnosticos: withDiagnostic.length,
      sinDiagnostico: withoutDiagnostic.length,
      promedioMadurez: avg,
      nivelPredominante: predominant ? MATURITY_LABELS[Number(predominant[0])] || `Nivel ${predominant[0]}` : 'N/A',
    };
  }, [withDiagnostic, withoutDiagnostic, MATURITY_LABELS]);

  const participantsByArea = useMemo(() =>
    areas.map((area, idx) => ({
      area,
      count: orgFiltered.filter(u => u.area === area).length,
      color: COLORS[idx % COLORS.length],
    })),
    [areas, orgFiltered]
  );

  const participantsByPosition = useMemo(() => {
    const positions: Record<string, number> = {};
    orgFiltered.forEach(u => {
      const pos = u.position || 'Sin cargo';
      positions[pos] = (positions[pos] || 0) + 1;
    });
    return Object.entries(positions).map(([cargo, count]) => ({ cargo, count })).sort((a, b) => b.count - a.count);
  }, [orgFiltered]);

  const participantsByMaturity = useMemo(() =>
    [1, 2, 3, 4, 5].map(level => ({
      nivel: MATURITY_LABELS[level],
      count: withDiagnostic.filter(u => u.maturity_level === level).length,
    })),
    [withDiagnostic, MATURITY_LABELS]
  );

  const maturityByAge = useMemo(() => {
    const ranges = ['20-25', '25-30', '30-40', '40-50', '50-60'];
    return ranges.map(range => {
      const inRange = withDiagnostic.filter(u => u.age_range === range);
      const avg = inRange.length > 0 ? inRange.reduce((a, u) => a + (u.maturity_level || 0), 0) / inRange.length : 0;
      return { ageRange: range, promedio: Math.round(avg * 10) / 10, count: inRange.length };
    }).filter(r => r.count > 0);
  }, [withDiagnostic]);

  const criticalLevel = useMemo(() => withDiagnostic.filter(u => u.maturity_level === 1), [withDiagnostic]);
  const lowParticipationAreas = useMemo(() => participantsByArea.filter(a => a.count < 3), [participantsByArea]);

  const selectedOrgName = organizationFilter !== 'all'
    ? organizations.find(o => o.id === organizationFilter)?.name : 'Todas las organizaciones';

  const handleExportExcel = () => {
    exportToExcel(areaFiltered.map(u => ({
      name: u.full_name,
      area: u.area || 'N/A',
      cargo: u.position || 'N/A',
      madurezIA: u.has_diagnostic ? (u.maturity_level || 0) : 'N/A',
      diagnostico: u.has_diagnostic ? 'Sí' : 'No',
    })), [
      { key: 'name', header: 'Nombre' },
      { key: 'area', header: 'Área' },
      { key: 'cargo', header: 'Cargo' },
      { key: 'madurezIA', header: 'Madurez IA' },
      { key: 'diagnostico', header: 'Diagnóstico' },
    ], {
      fileName: `metricas-diagnostico-${new Date().toISOString().split('T')[0]}`,
      title: 'Métricas de Diagnóstico',
    });
  };

  const handleExportPDF = () => {
    exportToPDF(areaFiltered.map(u => ({
      name: u.full_name,
      area: u.area || 'N/A',
      cargo: u.position || 'N/A',
      madurezIA: u.has_diagnostic ? String(u.maturity_level || 0) : 'N/A',
      diagnostico: u.has_diagnostic ? 'Sí' : 'No',
    })), [
      { key: 'name', header: 'Nombre' },
      { key: 'area', header: 'Área' },
      { key: 'cargo', header: 'Cargo' },
      { key: 'madurezIA', header: 'Madurez IA' },
      { key: 'diagnostico', header: 'Diagnóstico' },
    ], {
      fileName: `metricas-diagnostico-${new Date().toISOString().split('T')[0]}`,
      title: 'Métricas de Diagnóstico',
      subtitle: selectedOrgName,
    }, [
      { label: 'Total diagnósticos', value: stats.totalDiagnosticos },
      { label: 'Sin diagnóstico', value: stats.sinDiagnostico },
      { label: 'Promedio madurez', value: stats.promedioMadurez.toFixed(1) },
    ]);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Métricas de Diagnóstico</h1>
          <p className="text-muted-foreground">
            Perfil y nivel de madurez IA {isSuperAdmin && organizationFilter !== 'all' && `- ${selectedOrgName}`}
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {isSuperAdmin && (
            <Select value={organizationFilter} onValueChange={setOrganizationFilter}>
              <SelectTrigger className="w-[200px]">
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Organización" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las organizaciones</SelectItem>
                {organizations.map(org => (
                  <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={areaFilter} onValueChange={setAreaFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrar por área" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las áreas</SelectItem>
              {areas.map(area => (
                <SelectItem key={area} value={area}>{area}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />Excel
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <FileText className="w-4 h-4 mr-2" />PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card border-primary/30">
          <CardContent className="p-6">
            <Users className="w-8 h-8 text-primary mb-2" />
            <p className="text-3xl font-bold">{stats.totalDiagnosticos}</p>
            <p className="text-sm text-muted-foreground">Diagnósticos realizados</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-warning/30">
          <CardContent className="p-6">
            <UserX className="w-8 h-8 text-warning mb-2" />
            <p className="text-3xl font-bold text-warning">{stats.sinDiagnostico}</p>
            <p className="text-sm text-muted-foreground">Sin diagnóstico</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-secondary/30">
          <CardContent className="p-6">
            <TrendingUp className="w-8 h-8 text-secondary mb-2" />
            <p className="text-3xl font-bold">{stats.promedioMadurez.toFixed(1)}</p>
            <p className="text-sm text-muted-foreground">Promedio madurez IA</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-6">
            <Target className="w-8 h-8 text-accent mb-2" />
            <p className="text-lg font-bold">{stats.nivelPredominante}</p>
            <p className="text-sm text-muted-foreground">Nivel predominante</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-primary" />
              Participantes por Área
            </CardTitle>
          </CardHeader>
          <CardContent>
            {participantsByArea.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Sin datos</p>
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={participantsByArea} cx="50%" cy="50%" labelLine={false}
                      label={({ area, count }) => `${(area as string).split(' ')[0]}: ${count}`}
                      outerRadius={100} dataKey="count" nameKey="area">
                      {participantsByArea.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-secondary" />
              Participantes por Cargo
            </CardTitle>
          </CardHeader>
          <CardContent>
            {participantsByPosition.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Sin datos</p>
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={participantsByPosition} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" stroke="hsl(var(--muted-foreground))" />
                    <YAxis dataKey="cargo" type="category" width={120} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                    <Bar dataKey="count" fill="hsl(var(--secondary))" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-accent" />
              Nivel de Madurez IA
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={participantsByMaturity}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="nivel" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <YAxis stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {participantsByMaturity.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {maturityByAge.length > 0 && (
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Madurez Promedio por Edad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={maturityByAge}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="ageRange" stroke="hsl(var(--muted-foreground))" />
                    <YAxis domain={[0, 5]} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                    <Line type="monotone" dataKey="promedio" stroke="hsl(var(--primary))" strokeWidth={3} dot={{ fill: 'hsl(var(--primary))', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="glass-card border-destructive/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <UserX className="w-4 h-4 text-destructive" />
              Sin Diagnóstico ({withoutDiagnostic.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {withoutDiagnostic.length === 0 ? (
              <p className="text-sm text-muted-foreground">Todos completaron el diagnóstico</p>
            ) : (
              <div className="space-y-2">
                {withoutDiagnostic.slice(0, 10).map(u => (
                  <div key={u.id} className="flex items-center justify-between p-2 bg-destructive/10 rounded-lg text-sm">
                    <span>{u.full_name}</span>
                    <span className="text-muted-foreground">{u.area || 'N/A'}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="glass-card border-warning/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-warning" />
              Áreas con Baja Participación ({lowParticipationAreas.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lowParticipationAreas.length === 0 ? (
              <p className="text-sm text-muted-foreground">Todas las áreas tienen buena participación</p>
            ) : (
              <div className="space-y-2">
                {lowParticipationAreas.map(a => (
                  <div key={a.area} className="flex items-center justify-between p-2 bg-warning/10 rounded-lg text-sm">
                    <span>{a.area}</span>
                    <span className="text-warning font-medium">{a.count} participantes</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="glass-card border-destructive/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="w-4 h-4 text-destructive" />
              Nivel Crítico (Nivel 1) ({criticalLevel.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {criticalLevel.length === 0 ? (
              <p className="text-sm text-muted-foreground">No hay usuarios en nivel crítico</p>
            ) : (
              <div className="space-y-2">
                {criticalLevel.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-2 bg-destructive/10 rounded-lg text-sm">
                    <span>{u.full_name}</span>
                    <span className="text-muted-foreground">{u.position || 'N/A'}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
